// src/components/search/SearchSuggestions.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  Briefcase,
  Trophy,
  Users,
  TrendingUp,
  Building,
  Shield
} from 'lucide-react';
import { getSearchSuggestions } from '@/lib/supabase/search';

interface SearchSuggestionsProps {
  onClose?: () => void;
}

export default function SearchSuggestions({ onClose }: SearchSuggestionsProps) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        setSuggestions(await getSearchSuggestions());
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setError('Failed to load suggestions');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const go = (to: string, state?: any) => {
    navigate(to, state ? { state } : undefined);
    onClose?.();
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 bg-dark-300 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-dark-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Popular Members */}
      {suggestions.popularMembers?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <Users className="h-4 w-4 mr-2" />
            Most Connected
          </h3>
          <div className="space-y-2">
            {suggestions.popularMembers.map((m: any) => {
              // ─────────────────────────────────────────────
              // Subtract 1 from the raw connection count (to remove Lucy),
              // but never go below 0.
              const displayCount =
                typeof m.connectionCount === 'number' && m.connectionCount > 0
                  ? m.connectionCount - 1
                  : 0;
              // ─────────────────────────────────────────────

              return (
                <button
                  key={m.id}
                  onClick={() => go(`/dashboard/members/${m.id}`)}
                  className="w-full flex items-center p-2 hover:bg-dark-300 rounded-lg transition-colors"
                >
                  <img
                    src={m.image}
                    alt={m.name}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) =>
                      (e.currentTarget.src =
                        'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png')
                    }
                  />
                  <div className="ml-3 text-left">
                    <div className="text-white text-sm">{m.name}</div>
                    <div className="text-dark-800 text-xs">
                      {m.city || 'Unknown City'}
                      {/* Only show “connections” text if we have a numeric count */}
                      {typeof m.connectionCount === 'number' && (
                        <> • {displayCount} connections</>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Popular Clubs */}
      {suggestions.popularClubs?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <Shield className="h-4 w-4 mr-2" />
            Popular Clubs
          </h3>
          <div className="space-y-2">
            {suggestions.popularClubs.map((c: any) => (
              <button
                key={c.id}
                onClick={() => go(`/dashboard/clubs/${c.id}`)}
                className="w-full flex items-center p-2 hover:bg-dark-300 rounded-lg transition-colors"
              >
                {c.logo ? (
                  <img
                    src={c.logo}
                    alt={c.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-dark-300 border border-dark-400">
                    <Shield className="h-4 w-4 text-gold-400" />
                  </div>
                )}
                <div className="ml-3 text-left">
                  <div className="text-white text-sm">{c.name}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Active Cities */}
      {suggestions.cities?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <MapPin className="h-4 w-4 mr-2" />
            Active Cities
          </h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.cities.map((city: string) => (
              <button
                key={city}
                onClick={() => go('/dashboard/facilities', { selectedCity: city })}
                className="px-3 py-1 bg-dark-300 hover:bg-dark-400 rounded-full text-white text-sm transition-colors"
              >
                {city}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Top Sports */}
      {suggestions.topSports?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <Trophy className="h-4 w-4 mr-2" />
            Favorite Sports
          </h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.topSports.map((s: any) => (
              <button
                key={s.id}
                onClick={() =>
                  go('/dashboard/match', {
                    searchKey: Date.now(),
                    preSelectedSport: `${s.emoji} ${s.name}`,
                  })
                }
                className="px-3 py-1 bg-dark-300 hover:bg-dark-400 rounded-full text-white text-sm transition-colors"
              >
                {s.emoji} {s.name}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Popular Activities */}
      {suggestions.popularActivities?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            Popular Activities
          </h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.popularActivities.map((a: any) => (
              <button
                key={a.name}
                onClick={() =>
                  go('/dashboard/activities', { selectedSport: a.name })
                }
                className="px-3 py-1 bg-dark-300 hover:bg-dark-400 rounded-full text-white text-sm transition-colors"
              >
                {a.name}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Industries */}
      {suggestions.industries?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <Briefcase className="h-4 w-4 mr-2" />
            Industries
          </h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.industries.map((ind: string) => (
              <button
                key={ind}
                onClick={() => go('/dashboard/match', { searchTerm: ind })}
                className="px-3 py-1 bg-dark-300 hover:bg-dark-400 rounded-full text-white text-sm transition-colors"
              >
                {ind}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Popular Companies */}
      {suggestions.popularCompanies?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <Building className="h-4 w-4 mr-2" />
            Popular Companies
          </h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.popularCompanies.map((cmp: string) => (
              <button
                key={cmp}
                onClick={() => go(`/dashboard?search=${encodeURIComponent(cmp)}`)}
                className="px-3 py-1 bg-dark-300 hover:bg-dark-400 rounded-full text-white text-sm transition-colors"
              >
                {cmp}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Trending Posts */}
      {suggestions.trendingPosts?.length > 0 && (
        <section>
          <h3 className="text-dark-800 text-sm font-medium mb-2 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            Trending Posts
          </h3>
          <div className="space-y-2">
            {suggestions.trendingPosts.map((post: any) => (
              <button
                key={post.id}
                onClick={() => go(`/dashboard?post=${post.id}`)}
                className="w-full flex items-start p-2 hover:bg-dark-300 rounded-lg transition-colors"
              >
                <img
                  src={post.author.image}
                  alt={post.author.name}
                  className="w-8 h-8 rounded-full object-cover"
                  onError={(e) =>
                    (e.currentTarget.src =
                      'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png')
                  }
                />
                <div className="ml-3 text-left">
                  <div className="text-white text-sm font-medium">
                    {post.author.name}
                  </div>
                  <div className="text-dark-800 text-xs line-clamp-2">
                    {post.content}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}